import React from 'react';
import { useTranslation } from 'react-i18next';
import { Reorder } from 'motion/react';

import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Popover from '@mui/material/Popover';
import { alpha } from '@mui/material/styles';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';

import Iconify from '../../../theme/iconify/iconify';

import Section from './Section';
import { mislecAPI } from '../../../util/axios';

function SectionOrder({ course, saveCourse, sectionVisibility, setOpen, setMessage, setSeverity, updateUser }) {
    const [sectionOrder, setSectionOrder] = React.useState(null);
    const [tempOrder, setTempOrder] = React.useState(null);
    const [exportTypeMenu, setExportTypeMenu] = React.useState(null);

    const FORMAT = [
        {
            format: 'JSON',
            icon: 'si:json-alt-1-duotone',
        },
        {
            format: 'Markdown',
            icon: 'bi:markdown',
        },
        {
            format: 'PDF',
            icon: 'ion:document-outline',
        }
    ];

    const { t } = useTranslation();

    React.useEffect(() => {
        const order = course?.sections?.map((section) => ({
            id: section.id,
            title: section.title,
            visible: section.visible,
            type: section.subsections[0]?.quiz?.type === 'exam' ? 'exam' : 'content',
            exam: section.subsections[0]?.quiz?.type === 'exam' ? section.subsections[0]?.id : null
        }));
        setSectionOrder(order);
        setTempOrder(order);
    }, [course]);

    const reorder = () => {
        setSectionOrder(tempOrder);
    }

    const exportCourse = (type) => {
        if (type === 'PDF') {
            setMessage(t('course.tabs.settings.info.export'));
            setSeverity('info');
            setOpen(true);

            handleCloseMenu();

            //  Export course to PDF file
            mislecAPI.get(`/api/course/${course._id}/pdf`, { responseType: 'blob' })
                .then((response) => {
                    const url = window.URL.createObjectURL(response.data);
                    const downloadAnchorNode = document.createElement('a');
                    downloadAnchorNode.setAttribute('href', url);
                    downloadAnchorNode.setAttribute('download', `mISLec_course_${course._id}.pdf`);
                    document.body.appendChild(downloadAnchorNode);
                    downloadAnchorNode.click();
                    downloadAnchorNode.remove();
                    window.URL.revokeObjectURL(url);
                })
                .catch((error) => {
                    setMessage(t('api.general.500'));
                    setSeverity('error');
                    setOpen(true);
                });
        } else if (type === 'JSON') {
            //  Export course to JSON file
            const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(course, null, 2));
            const downloadAnchorNode = document.createElement('a');
            downloadAnchorNode.setAttribute('href', dataStr);
            downloadAnchorNode.setAttribute('download', `mISLec_course_${course._id}.json`);
            document.body.appendChild(downloadAnchorNode);
            downloadAnchorNode.click();
            downloadAnchorNode.remove();
            handleCloseMenu();
        } else if (type === 'Markdown') {
            //  Export course to Markdown file
            mislecAPI.get(`/api/course/${course._id}/markdown`, { responseType: 'blob' })
                .then((response) => {
                    const url = window.URL.createObjectURL(response.data);
                    const downloadAnchorNode = document.createElement('a');
                    downloadAnchorNode.setAttribute('href', url);
                    downloadAnchorNode.setAttribute('download', `mISLec_course_${course._id}.zip`);
                    document.body.appendChild(downloadAnchorNode);
                    downloadAnchorNode.click();
                    downloadAnchorNode.remove();
                    window.URL.revokeObjectURL(url);
                })
                .catch((error) => {
                    setMessage(t('api.general.500'));
                    setSeverity('error');
                    setOpen(true);
                });
            handleCloseMenu();
        }
    }

    const handleOpenMenu = (event) => {
        setExportTypeMenu(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setExportTypeMenu(null);
    }

    return (
        <>
            <Grid item xs={12} sx={{ pt: '0px !important' }}>
                <Typography variant='h5'>{t('course.tabs.settings.sections.title')}</Typography>
                <Typography variant='body2' sx={{ mt: 1 }}>
                    {t('course.tabs.settings.sections.description')}
                </Typography>
            </Grid>
            {sectionOrder &&
                <Grid item xs={12} sx={{ pt: '0px !important' }}>
                    <Reorder.Group
                        className='mISLec_course_answers'
                        axis='y'
                        values={sectionOrder}
                        onReorder={(e) => { setTempOrder(e); }}
                    >
                        {sectionOrder?.map((section, index) => (
                            <Section section={section} key={section.id} index={index} reorder={reorder} sectionVisibility={sectionVisibility} setOpen={setOpen} setMessage={setMessage} setSeverity={setSeverity} updateUser={updateUser} />
                        ))}
                    </Reorder.Group>
                </Grid>
            }
            <Grid item xs={12} sx={{ pt: '8px !important' }}>
                <Grid
                    container
                    alignItems='start'
                    wrap='nowrap'
                    onClick={handleOpenMenu}
                    sx={{
                        display: 'inline-flex',
                        cursor: 'pointer',
                        p: 0.5,
                        fontSize: '0.8rem',
                        width: 'auto',
                        '&:hover': {
                            backgroundColor: 'rgba(145, 158, 171, 0.08)',
                            borderRadius: '8px'
                        }
                    }}
                >
                    <Grid item sx={{ mr: 1.5, mt: 0.5, display: 'flex', alignItems: 'center' }}>
                        <Iconify icon='material-symbols:download' sx={{ color: 'primary.main' }} />
                    </Grid>
                    <Grid item>
                        <Typography variant='body2' sx={{ fontWeight: '500' }}>
                            {t('course.tabs.settings.information.inputs.export.label')}
                        </Typography>
                        <Typography variant='caption' color='text.secondary'>
                            {t('course.tabs.settings.information.inputs.export.description')}
                        </Typography>
                    </Grid>
                </Grid>
                <Popover
                    open={!!exportTypeMenu}
                    anchorEl={exportTypeMenu}
                    onClose={handleCloseMenu}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    PaperProps={{
                        sx: {
                            p: 0,
                            mt: 1,
                            ml: 0.75,
                            width: 150,
                        },
                    }}
                >
                    {FORMAT.map((format) => (
                        <MenuItem
                            onClick={() => exportCourse(format.format)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                my: 0.5,
                                px: 2,
                                color: 'text.primary',
                            }}
                        >
                            <Iconify icon={format.icon} sx={{ color: 'primary.main' }} />
                            <Typography variant='body2' sx={{ color: 'text.primary', fontSize: '0.8rem', ml: 1 }}>
                                {format.format}
                            </Typography>
                        </MenuItem>
                    ))}
                </Popover>
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' }, pt: '16px !important' }}>
                    <Button
                        variant=''
                        onClick={() => saveCourse(sectionOrder.map((section) => section.id))}
                        sx={{
                            borderRadius: '8px !important',
                            minWidth: {
                                xs: '100%',
                                md: 180
                            },
                            maxWidth: {
                                xs: '100%',
                                md: 180
                            },
                            backgroundColor: (theme) => ({
                                xs: alpha(theme.palette.primary.main, 0.08),
                                md: 'rgba(33, 43, 54, 0.08)'
                            }),
                            color: {
                                xs: 'primary.main',
                                md: 'inherit'
                            },
                            '&:hover': {
                                color: 'primary.main',
                                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08)
                            }
                        }}
                    >
                        {t('course.tabs.settings.buttons.save')}
                    </Button>
                </Grid>
            </Grid>
            <Grid item xs={12} sx={{ mb: 2, mt: 0, pt: '32px !important' }}>
                <Divider />
            </Grid>
        </>
    );
}

export default SectionOrder;